import React from "react";
import { Box, Group, Stack, Text, Title } from "@mantine/core";
import styles from "./SettingsSection.module.css";

export interface SettingsSectionProps {
  /** Section heading (rendered as an order-5 title, weight 600). */
  title?: React.ReactNode;
  /** Sub-line under the heading. */
  description?: React.ReactNode;
  /** Right-aligned section controls. */
  actions?: React.ReactNode;
  /** Draw the 1px section border. Defaults to true. */
  withBorder?: boolean;
  /** Section body. */
  children?: React.ReactNode;
  /** Escape hatch. */
  className?: string;
}

/**
 * A bordered section card for grouping related settings: an optional heading
 * row (title + description on the left, actions on the right) above the body.
 * Border/radius/padding/surface all come from the --settings-* tokens, so
 * sections look identical across every settings page.
 */
export default function SettingsSection({
  title,
  description,
  actions,
  withBorder = true,
  children,
  className,
}: SettingsSectionProps) {
  const hasHeader = Boolean(title || description || actions);
  return (
    <Box
      className={[styles.section, withBorder && styles.bordered, className]
        .filter(Boolean)
        .join(" ")}
    >
      {hasHeader && (
        <Group
          justify="space-between"
          align="flex-start"
          wrap="nowrap"
          gap="md"
          className={styles.header}
        >
          {(title || description) && (
            <Stack gap={2} className={styles.titleBlock}>
              {title && (
                <Title order={5} fw={600} className={styles.title}>
                  {title}
                </Title>
              )}
              {description && (
                <Text size="sm" c="dimmed">
                  {description}
                </Text>
              )}
            </Stack>
          )}
          {actions && <Box className={styles.actions}>{actions}</Box>}
        </Group>
      )}
      {children}
    </Box>
  );
}
